import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable, Subject, combineLatest, filter, map, shareReplay, switchMap, take, tap } from 'rxjs'
import {
   PIMSProduct,
   ProductType2,
   ProductsClient,
   UpdateProductMappingRequest,
   VetCorProduct,
   VetCorProductCategory,
} from '../shared/services/ppv.api'
import { ProductFunctions } from './functions'
import { AuthManager } from '../shared/auth/AuthManager'

@Injectable({ providedIn: 'root' })
export class ProductsService {
   private _loadVCProducts$ = new Subject<void>()

   private _allCategories$ = new BehaviorSubject<VetCorProductCategory[]>(undefined)
   allCategories$ = this._allCategories$.pipe(shareReplay(1))

   private _allVCProducts$ = new BehaviorSubject<VetCorProduct[]>(undefined)
   allVCProducts$ = combineLatest([this._allVCProducts$, this.allCategories$]).pipe(
      filter(([prods, cats]) => !!prods && !!cats),
      map(([prods, cats]) => {
         const catLookup = {}
         cats.forEach((cat) => catLookup[cat.vcProductCategorySKey])
         prods.forEach((p) => {
            if (p.vcProductCategorySKey) p.vcProductCategory = catLookup[p.vcProductCategorySKey]
         })

         return prods
      }),
      shareReplay(1)
   )

   hierarchicalProducts$ = combineLatest([this.allVCProducts$, this.allCategories$]).pipe(
      map(([prods, cats]) => ProductFunctions.getTreeSelectProducts(prods, cats, true)),
      shareReplay(1)
   )

   hierarchicalProductsWithoutMappedPercentage$ = combineLatest([this.allVCProducts$, this.allCategories$]).pipe(
      map(([prods, cats]) => ProductFunctions.getTreeSelectProducts(prods, cats, false)),
      shareReplay(1)
   )

   hierarchicalProductsWithCodes$ = combineLatest([this.allVCProducts$, this.allCategories$]).pipe(
      map(([prods, cats]) => ProductFunctions.getTreeSelectProducts(prods, cats, false, true)),
      shareReplay(1)
   )

   private _PIMSProducts$ = new BehaviorSubject<PIMSProduct[]>(undefined)
   pimsProducts$ = this._PIMSProducts$.asObservable()

   private _loading$ = new BehaviorSubject<boolean>(true)
   loading$ = this._loading$.asObservable()
   hierarchicalPracticeProducts$: { [practiceSKey: number]: Observable<any[]> } = {}
   practiceProducts$: { [practiceSKey: number]: Observable<PIMSProduct[]> } = {}

   loadVCProducts() {
      this._loadVCProducts$.next()
   }

   getPracticeProducts(practiceSKey: number) {
      if (!this.practiceProducts$[practiceSKey]) {
         this.practiceProducts$[practiceSKey] = this.api
            .searchPIMSProducts({
               practiceSKey: practiceSKey,
               calculateTotalRevenuePercent: true,
            })
            .pipe(shareReplay(1))
      }
      return this.practiceProducts$[practiceSKey]
   }

   getHierarchicalPracticeProducts(practiceSKey: number) {
      if (!this.hierarchicalPracticeProducts$[practiceSKey]) {
         this.hierarchicalPracticeProducts$[practiceSKey] = this.getPracticeProducts(practiceSKey).pipe(
            map((products) => {
               return ProductFunctions.getPIMSTreeSelectProducts(products)
            }),
            shareReplay(1)
         )
      }
      return this.hierarchicalPracticeProducts$[practiceSKey]
   }

   constructor(private api: ProductsClient, private authManager: AuthManager) {
      this._loadVCProducts$
         .pipe(
            take(1),
            switchMap((_) =>
               this.api.searchVCProducts({
                  skip: 0,
                  take: 2147483647,
               })
            ),
            tap((rows) => {
               this._allVCProducts$.next(rows)
               this._loading$.next(false)
            })
         )
         .subscribe()

      this.api
         .getVCProductCategories()
         .pipe(
            take(1),
            tap((cats) => this._allCategories$.next(cats))
         )
         .subscribe()
   }
}
